import { ApplicationConfig, computed, importProvidersFrom, inject, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { routes } from './app.routes';
import { LoggerModule } from 'ngx-logger';
import { environment } from '../environments/environment';
import { HttpClientInMemoryWebApiModule } from 'angular-in-memory-web-api';
import { InMemoryDataService } from './core/services/in-memory-data.service';
import { API_HOST, provideOnrsrUi } from '@onrsr/ui/core';
import { LAYOUT_AUTH_SERVICE, LAYOUT_PROFILE_SERVICE, LAYOUT_BRANDING } from '@onrsr/ui/layout';
import { AuthService, provideAuth } from '@onrsr/ui/auth';
import { ProfileService } from './core/services/profile.service';
import { provideAppAbility } from './core/auth/ability';
import { unauthorizedInterceptor } from './core/auth/unauthorized.interceptor';
import { tablerChartBar, tablerLayoutGridAdd, tablerUsers, tablerBuilding, tablerSettings } from '@ng-icons/tabler-icons';
import { provideIcons } from '@ng-icons/core';

export const appConfig: ApplicationConfig = {
  providers: [
    provideIcons({ tablerChartBar, tablerLayoutGridAdd, tablerUsers, tablerBuilding, tablerSettings }),
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withXhr(), withInterceptors([unauthorizedInterceptor]), withInterceptorsFromDi()),
    ...provideOnrsrUi(),
    provideAppAbility(),
    provideAuth({
      clientId: environment.azure.clientId,
      authority: `https://login.microsoftonline.com/${environment.azure.tenantId}`,
      knownAuthorities: [],
      redirectUri: environment.azure.redirectUri,
      postLogoutRedirectUri: environment.azure.redirectUri,
      scopes: [`api://${environment.azure.clientId}/access_as_user`],
      protectedResources: new Map<string, string[]>([
        ['https://graph.microsoft.com/v1.0/*', ['user.read', 'profile']],
        [`${environment.apiHost}/*`, [`api://${environment.azure.clientId}/access_as_user`]],
      ]),
    }),
    importProvidersFrom(
      HttpClientInMemoryWebApiModule.forRoot(InMemoryDataService, {
        apiBase: 'api/v1.0/',
        passThruUnknownUrl: true,
      }),
      LoggerModule.forRoot({
        serverLoggingUrl: environment.logging.apiEndpoint,
        level: environment.logging.level,
        serverLogLevel: environment.logging.serverLogLevel,
      })
    ),
    {
      provide: LAYOUT_BRANDING,
      useValue: { logoUrl: 'images/irg-review-logo.png', title: 'IRG Review' },
    },
    {
      provide: LAYOUT_AUTH_SERVICE,
      useExisting: AuthService,
    },
    {
      provide: LAYOUT_PROFILE_SERVICE,
      useFactory: () => {
        const authService = inject(AuthService);
        const profileService = inject(ProfileService);
        return {
          fullName: computed(() => {
            const user = authService.currentUser();
            return user?.name ?? user?.username ?? '';
          }),
          avatarImage: computed(() => profileService.avatarUrl() ?? null),
        };
      },
    },
    { provide: API_HOST, useValue: environment.apiHost },
  ],
};
